import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from './firebase';
import s from './styles';
import logo from './assets/logo.png';
import pharmacistPhoto1 from './assets/pharmacists/pharmacist-1.jpg';
import pharmacistPhoto2 from './assets/pharmacists/pharmacist-2.jpg';
import pharmacistPhoto3 from './assets/pharmacists/pharmacist-3.jpg';
import pharmacistPhoto4 from './assets/pharmacists/pharmacist-4.jpg';
import pharmacistPhoto5 from './assets/pharmacists/pharmacist-5.jpg';

// All 5 are bundled at build time (no network fetch at runtime — this is
// what actually makes the hero photo appear instantly instead of waiting on
// an external image host), one picked at random per page load for variety.
const PHARMACIST_PHOTOS = [pharmacistPhoto1, pharmacistPhoto2, pharmacistPhoto3, pharmacistPhoto4, pharmacistPhoto5];

export default function LoginPage({ onSwitch }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);

  // All 5 photos are already in the page (bundled, not fetched), so this is
  // just swapping which one is visible — no loading delay between slides.
  useEffect(() => {
    const id = setInterval(() => {
      setActivePhoto(i => (i + 1) % PHARMACIST_PHOTOS.length);
    }, 4500);
    return () => clearInterval(id);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please enter your email and password'); return; }
    setLoading(true); setError('');
    try {
      await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
    } catch (err) {
      const msg =
        err.code === 'auth/user-not-found' ? 'No account found with this email. Please register first.' :
        err.code === 'auth/wrong-password' ? 'Incorrect password. Please check and try again.' :
        err.code === 'auth/invalid-email' ? 'Please enter a valid email address.' :
        err.code === 'auth/invalid-credential' ? 'Incorrect email or password. Please try again.' :
        err.code === 'auth/too-many-requests' ? 'Too many failed attempts. Please wait a few minutes and try again.' :
        err.code === 'auth/user-disabled' ? 'This account has been disabled. Contact pharmdev.org@gmail.com.' :
        'Login failed. Please check your email and password.';
      setError(msg);
    } finally { setLoading(false); }
  };

  return (
    <div style={{ height: '100vh', display: 'flex', backgroundColor: '#FFF', overflow: 'hidden' }}>
      <style>{`@media (max-width: 860px) { .login-hero-panel { display: none !important; } }`}</style>

      {/* HERO PANEL */}
      <div className="login-hero-panel" style={{ flex: '1 1 480px', position: 'relative', overflow: 'hidden', backgroundColor: '#1D9E75' }}>
        {PHARMACIST_PHOTOS.map((photo, i) => (
          <img
            key={photo}
            src={photo}
            alt=""
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'top center',
              opacity: i === activePhoto ? 1 : 0,
              transition: 'opacity 1s ease-in-out',
            }}
          />
        ))}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(15,25,35,0.60) 0%, rgba(15,25,35,0.05) 30%, rgba(15,25,35,0.05) 55%, rgba(15,25,35,0.75) 100%)' }} />
        <div style={{ position: 'absolute', left: 40, right: 40, bottom: 56 }}>
          <p style={{ fontSize: 32, fontWeight: 800, color: '#FFF', lineHeight: 1.25, margin: 0, textShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
            Run Your Pharmacy,<br/>Simplified
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            {PHARMACIST_PHOTOS.map((photo, i) => (
              <div
                key={photo}
                style={{
                  width: i === activePhoto ? 22 : 8, height: 8, borderRadius: 4,
                  backgroundColor: i === activePhoto ? '#FFF' : 'rgba(255,255,255,0.4)',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* FORM */}
      <div style={{ flex: '1 1 460px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', overflowY: 'auto' }}>
      <div style={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>
        <img src={logo} alt="PharmDev" style={{ width: 200, marginBottom: 12 }} />
        <p style={{ ...s.authSub, marginTop: -4 }}>Sign in to manage your pharmacy orders, inventory and earnings</p>

        <form onSubmit={handleLogin} style={{ width: '100%', textAlign: 'left' }}>

          {/* EMAIL */}
          <div style={s.fieldGroup}>
            <label style={s.label}>Email Address</label>
            <input
              style={s.input}
              type="email"
              placeholder="pharmacy@email.com"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              autoComplete="email"
            />
          </div>

          {/* PASSWORD WITH EYE */}
          <div style={s.fieldGroup}>
            <label style={s.label}>Password</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                style={{ ...s.input, paddingRight: 48 }}
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                autoComplete="current-password"
              />
              <button
                type="button"
                style={{
                  position: 'absolute', right: 12,
                  background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: 18, padding: 4,
                  color: '#888', lineHeight: 1,
                }}
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {/* ERROR */}
          {error && (
            <div style={{ backgroundColor: '#FEE2E2', borderRadius: 8, padding: 12, marginBottom: 14, border: '1px solid #FCA5A5' }}>
              <p style={{ color: '#B91C1C', fontSize: 13, margin: 0 }}>⚠️ {error}</p>
            </div>
          )}

          {/* SUBMIT */}
          <button
            type="submit"
            style={{ ...s.loginBtn, opacity: loading ? 0.6 : 1 }}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In →'}
          </button>
        </form>

        {/* PENDING NOTICE */}
        <div style={{ backgroundColor: '#FEF3C7', borderRadius: 10, padding: 12, marginTop: 16, border: '1px solid #FCD34D' }}>
          <p style={{ fontSize: 12, color: '#92400E', margin: 0 }}>
            ⏳ <strong>Just registered?</strong> Your account needs admin approval before you can sign in.
            You will receive an email when approved — usually within 24 hours.
          </p>
        </div>

        {/* REGISTER LINK */}
        <div style={{ borderTop: '1px solid #F3F4F6', marginTop: 20, paddingTop: 20, width: '100%' }}>
          <p style={{ fontSize: 13, color: '#888', margin: '0 0 12px', textAlign: 'center' }}>
            New pharmacy? Register to join PharmDev
          </p>
          <button
            style={{ ...s.loginBtn, backgroundColor: '#F7F8FA', color: '#1D9E75', border: '1.5px solid #1D9E75' }}
            onClick={onSwitch}
          >
            Register Your Pharmacy →
          </button>
        </div>

        <div style={{ ...s.infoBox, marginTop: 16 }}>
          <p style={{ ...s.infoText, textAlign: 'center' }}>
            🔒 Your data is secure and encrypted. Only FDA-licensed pharmacies are approved.
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}